import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Notice, Tag } from '../../../common/components/detail/detail';
import Picker from '../../../common/components/picker/picker';
import { Gate } from '../../../global/contexts/auth/auth';
import { useProjectName, useRepositoryName } from '../../../global/contexts/lookups/lookups';
import { resolveProject, searchProjects } from '../../projects/contexts/projects';
import { PLANS_PAGE_SIZE, usePlansList } from '../contexts/plans';
import { VERSION_STATE_LABEL, scopeLabel } from '../utils/form';

import '../styles/plans.css';

// Los planes, en dos contextos y con un solo componente.
//
//   · Suelto, en /admin/plans: entre proyectos, con su filtro de proyecto.
//   · Dentro de un proyecto, en su pestaña de Planeación: atado a ese proyecto,
//     sin filtro, porque el proyecto ya lo eligió la pantalla de arriba.
//
// `lockedProject` es lo que los separa. Dos componentes para esto habrían sido
// dos listas que se van pareciendo cada vez menos.
//
// LA DISTINCIÓN QUE ESTA LISTA TIENE QUE HACER: la ÚLTIMA versión y la APROBADA
// no son la misma, y dejan de serlo en cuanto alguien abre el siguiente
// borrador. Un tier construido desde la v1 sigue leyendo la v1 después de que
// se apruebe la v2 — así que una lista que mostrara solo «la versión actual»
// estaría describiendo un plan distinto del que salió el trabajo.

const STATE_TONE = { approved: 'active', in_review: 'accent', draft: 'warn' };

// EL FILTRO DE PROYECTO ES DEL SERVIDOR; el de estado, no.
//
// `GET /admin/plans` toma `limit`, `offset` y `project_id`. No toma estado ni
// texto, así que filtrar por estado corre sobre la página cargada y la pantalla
// lo dice — la misma regla que en usuarios, equipos y repositorios.
const STATE_FILTERS = [
    { value: '',          label: 'Todos los estados' },
    { value: 'draft',     label: 'En borrador' },
    { value: 'in_review', label: 'Presentados' },
    { value: 'approved',  label: 'Con versión aprobada' },
];

// El estado que representa al plan en la lista: el de su última versión, que es
// donde está la acción. La aprobada se muestra aparte, siempre.
function latestStateOf(plan) {
    return plan.latest_version?.state ?? plan.state ?? null;
}

function hasApproved(plan) {
    return Boolean(plan.approved_version ?? plan.approved_version_number);
}

export default function PlansOverview({ projectId: lockedProject }) {
    const [params, setParams] = useSearchParams();
    const projectName = useProjectName();
    const repoName = useRepositoryName();

    const standalone = lockedProject == null;
    const projectId = standalone ? (params.get('project') || '') : String(lockedProject);
    const state = params.get('state') || '';
    const page = Math.max(Number(params.get('page')) || 1, 1);
    const offset = (page - 1) * PLANS_PAGE_SIZE;

    const { data, isLoading, isFetching, error } = usePlansList({
        projectId,
        limit: PLANS_PAGE_SIZE,
        offset,
    });

    const loaded = useMemo(() => data?.items ?? [], [data]);
    const total = data?.pagination?.total ?? loaded.length;

    const plans = useMemo(() => {
        if (!state) return loaded;
        if (state === 'approved') return loaded.filter(hasApproved);
        return loaded.filter(plan => latestStateOf(plan) === state);
    }, [loaded, state]);

    const lastPage = useMemo(
        () => Math.max(Math.ceil(total / PLANS_PAGE_SIZE), 1),
        [total],
    );

    const setParam = (key, value) => {
        const target = new URLSearchParams(params);
        if (value) target.set(key, value);
        else target.delete(key);
        target.delete('page');
        setParams(target);
    };

    const goToPage = (next) => {
        const target = new URLSearchParams(params);
        if (next <= 1) target.delete('page');
        else target.set('page', String(next));
        setParams(target);
    };

    const newPlanHref = `/admin/plans/new${projectId ? `?project=${projectId}` : ''}`;

    if (isLoading && !data) return <p className="plans__loading">Cargando planes...</p>;

    if (error) {
        return (
            <div className="plans">
                <p className="plans__failure">
                    {error.status === 403
                        ? 'No tenés permiso para leer planes.'
                        : error.message || 'No se pudieron cargar los planes'}
                </p>
            </div>
        );
    }

    return (
        <div className="plans">
            <header className="plans__head">
                <p className="plans__count">
                    {total} plan{total === 1 ? '' : 'es'}
                    {/* «Ya filtrado por alcance», dice el contrato: el total es
                        el de lo que esta cuenta puede ver de verdad, no el de
                        la tabla. Vale decirlo donde se lee el número. */}
                    <span className="plans__hint"> · dentro de tu alcance</span>
                </p>
                <Gate domain="plans:write">
                    <Link to={newPlanHref} className="plans__action">Nuevo plan</Link>
                </Gate>
            </header>

            <div className="plans__filters">
                {standalone && (
                    <label className="plans__filter-field">
                        <span className="plans__filter-label">Proyecto</span>
                        <Picker
                            value={projectId}
                            onChange={(id) => setParam('project', id)}
                            fetchOptions={searchProjects}
                            resolveOption={resolveProject}
                            cacheKey="projects"
                            labelOf={project => project.name}
                            subtitleOf={project => project.slug}
                            placeholder="Todos los proyectos"
                            emptyLabel="Todos los proyectos"
                        />
                    </label>
                )}

                <label className="plans__filter-field plans__filter-field--narrow">
                    <span className="plans__filter-label">Estado</span>
                    <select
                        className="plans__filter-input"
                        value={state}
                        onChange={e => setParam('state', e.target.value)}
                    >
                        {STATE_FILTERS.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </label>
            </div>

            {/* El filtro de proyecto lo aplica el backend; el de estado, no. */}
            {state && total > loaded.length && (
                <p className="plans__scope-note">
                    El estado se aplica sobre <strong>esta página</strong> de {loaded.length}, no
                    sobre los {total} planes: el listado del backend filtra por proyecto pero no
                    por estado.
                </p>
            )}

            {plans.length === 0 && (
                <Notice>
                    {state && loaded.length
                        ? 'Ningún plan de esta página está en ese estado.'
                        : projectId
                            ? 'Este proyecto todavía no tiene ningún plan. Un plan es de dónde salen los tiers: sin una versión aprobada no se puede crear uno.'
                            : 'Todavía no hay planes. Un plan es de dónde salen los tiers: sin una versión aprobada no se puede crear uno.'}
                </Notice>
            )}

            {plans.length > 0 && (
                <ul className={`plans__list ${isFetching ? 'plans__list--refreshing' : ''}`}>
                    {plans.map(plan => {
                        const latest = plan.latest_version;
                        const approved = plan.approved_version;

                        return (
                            <li key={plan.id} className="plans__row">
                                <Link to={`/admin/plans/${plan.id}`} className="plans__link">
                                    <span className="plans__name">{plan.name}</span>

                                    <span className="plans__tags">
                                        {/* DOS LECTURAS, NUNCA UNA SOLA INSIGNIA.
                                            Un tier atado a la v1 sigue leyendo la
                                            v1 después de que se apruebe la v2. */}
                                        {approved
                                            ? (
                                                <Tag tone="active">
                                                    Aprobada: v{approved.number ?? approved}
                                                </Tag>
                                            )
                                            : <Tag tone="warn">Sin versión aprobada</Tag>}

                                        {latest && (
                                            <Tag tone={STATE_TONE[latest.state] ?? 'neutral'}>
                                                Última: v{latest.number}
                                                {latest.state
                                                    ? ` · ${VERSION_STATE_LABEL[latest.state] ?? latest.state}`
                                                    : ''}
                                            </Tag>
                                        )}

                                        <Tag>{scopeLabel(plan.repository_ids, plan.repository_ids?.map(repoName))}</Tag>

                                        {/* En la lista suelta el proyecto ubica;
                                            dentro de su pestaña sería la misma
                                            palabra en todas las filas. */}
                                        {standalone && plan.project_id != null && (
                                            <Tag>{projectName(plan.project_id)}</Tag>
                                        )}
                                    </span>
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            )}

            {total > PLANS_PAGE_SIZE && (
                <nav className="plans__pager">
                    <button
                        type="button"
                        className="plans__button"
                        onClick={() => goToPage(page - 1)}
                        disabled={page <= 1}
                    >
                        Anterior
                    </button>
                    <span className="plans__note">Página {page} de {lastPage}</span>
                    <button
                        type="button"
                        className="plans__button"
                        onClick={() => goToPage(page + 1)}
                        disabled={page >= lastPage}
                    >
                        Siguiente
                    </button>
                </nav>
            )}
        </div>
    );
}
